import React from 'react';
import CardGroupHeaderResponsive from './components/CardGroupHeaderResponsive/CardGroupHeaderResponsive';
import { Points } from 'components/Points/Points';
import { AssetUrls } from 'config/assets';
import MagicButton from 'components/MagicButton/MagicButton';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import './CardGroupContainerResponsive.css';

interface CardItemProps {
  image: string;
  points: number;
}

const CardItem: React.FC<CardItemProps> = ({ image, points }) => (
  <div className="card-item">
    <img className="card-item-image" src={image} alt="card" />
    <div className="card-item-points">
      <Points points={points} type="small" />
    </div>
  </div>
);

interface CardGroupContainerResponsiveProps {
  title: string;
  artistName?: string;           // For printings mode
  userName?: string;             // For send mode
  userAvatar?: string;           // For send mode
  userCountry?: string;          // For send mode - country code
  userPoints?: number;           // For send mode
  userIsElite?: boolean;         // For send mode - elite status
  userShowAnimatedBorder?: boolean; // For send mode - animated border
  userBorderColor1?: string | null; // For send mode - custom border color 1
  userBorderColor2?: string | null; // For send mode - custom border color 2
  lowPrice?: number;            // For printings
  highPrice?: number;           // For printings
  totalValue?: number;          // For send page
  autoMatch?: boolean;          // For send page
  onAutoMatchToggle?: (val: boolean) => void; // For send page
  autoMatchDisabled?: boolean;  // When global auto-match is on
  matchingCardCount?: number;   // For auto-match count display
  totalCardCount?: number;      // For auto-match count display
  mode?: 'printings' | 'send';
  onRemoveAll?: () => void;
  onSend?: () => void;
  onReset?: () => void;
  profileUrl?: string;           // URL for navigating to user profile (deprecated, use username)
  username?: string;             // Username for hover card + profile link
  isRemoved?: boolean;
  /** Per-package control: after the value pill on desktop, right of the username on mobile. */
  headerAction?: React.ReactNode;
  children?: React.ReactNode;
}

const CardGroupContainerResponsive: React.FC<CardGroupContainerResponsiveProps> = ({
  title,
  artistName,
  userName,
  userAvatar,
  userCountry,
  userPoints,
  userIsElite,
  userShowAnimatedBorder,
  userBorderColor1,
  userBorderColor2,
  lowPrice = 0,
  highPrice = 0,
  totalValue = 0,
  autoMatch = false,
  onAutoMatchToggle,
  autoMatchDisabled = false,
  matchingCardCount,
  totalCardCount,
  mode = 'printings',
  onRemoveAll,
  onSend,
  onReset,
  profileUrl,
  username,
  isRemoved = false,
  headerAction,
  children,
}) => {
  const displayName = mode === 'send' ? userName || 'Unknown User' : artistName || 'Unknown Artist';

  const placeholderCards = [
    { image: AssetUrls.sampleCards.llanowarElves, points: 1903000 },
    { image: AssetUrls.sampleCards.timeWalk, points: 1903000 },
    { image: AssetUrls.sampleCards.serraAngel, points: 1903000 },
    { image: AssetUrls.sampleCards.territorialHellkite, points: 1903000 },
  ];

  return (
    <div className="card-group-container-responsive">
      {/* Mobile: User Info Bar (shows on mobile, hidden on desktop) */}
      <div className="responsive-user-info-bar">
        {/* Row 1: Avatar + Username */}
        <div className="user-info-row-1-responsive">
          <AvatarWithFlag
            avatarUrl={userAvatar}
            username={displayName}
            countryCode={userCountry}
            size={48}
            isElite={userIsElite}
            showAnimatedBorder={userShowAnimatedBorder}
            borderColor1={userBorderColor1}
            borderColor2={userBorderColor2}
          />
          {username ? (
            <UsernameLink username={username} isElite={userIsElite} className="user-name-responsive user-name--clickable" />
          ) : profileUrl ? (
            <a href={profileUrl} className="user-name-responsive user-name--clickable">{displayName}</a>
          ) : (
            <p className="user-name-responsive">{displayName}</p>
          )}
          {headerAction && <div className="user-info-row-1-action-responsive">{headerAction}</div>}
        </div>

        {/* Row 2: Points Badge + Auto-Match Toggle */}
        <div className="user-info-row-2-responsive">
          <div className="points-badge-responsive">
            <Points points={userPoints || 0} type="small" />
          </div>
          <div className={`auto-match-container-responsive ${autoMatchDisabled ? 'disabled' : ''}`}>
            <span className="auto-match-label">
              &#9889; Auto-Match
              {matchingCardCount !== undefined && totalCardCount !== undefined && (
                <span className="auto-match-count">
                  ({matchingCardCount}/{totalCardCount})
                </span>
              )}
            </span>
            <label className="auto-match-toggle-responsive">
              <input
                type="checkbox"
                checked={autoMatch}
                onChange={(e) => onAutoMatchToggle?.(e.target.checked)}
                disabled={autoMatchDisabled}
              />
              <span className={`toggle-slider-responsive ${autoMatchDisabled ? 'forced-on' : ''}`} />
            </label>
          </div>
        </div>

        {/* Row 3: Remove All Button */}
        {onRemoveAll && (
          <div className="user-info-row-3-responsive">
            <MagicButton
              label={isRemoved ? "Reset" : "Remove All"}
              icon={
                isRemoved ? (
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/reset.svg"
                    alt="Reset"
                    style={{ width: 16, height: 16 }}
                  />
                ) : (
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                    alt="Remove"
                    style={{ width: 16, height: 16 }}
                  />
                )
              }
              color="dark-grey"
              fullWidth={true}
              onPress={isRemoved ? (onReset || (() => {})) : onRemoveAll}
            />
          </div>
        )}

        {/* Row 4: Send Button */}
        <div className="user-info-row-4-responsive">
          <MagicButton
            label="Send"
            icon={
              <img
                src={AssetUrls.icons.misc.sendPackage}
                alt="Send Icon"
                style={{ width: 20, height: 20 }}
              />
            }
            color="purple"
            fullWidth={true}
            onPress={onSend || onRemoveAll || (() => {})}
          />
        </div>

        {/* Row 5: Total Package Value */}
        <div className="user-info-row-5-responsive">
          <Points points={totalValue} type="small" />
        </div>
      </div>

      {/* Desktop: Original Left Sidebar (hidden on mobile, shown on desktop) */}
      <div className="card-group-left-responsive">
        <div className="artist-image-responsive">
          <div className="artist-top-info-responsive">
            <div className="user-info-desktop">
              <AvatarWithFlag
                avatarUrl={userAvatar}
                username={displayName}
                countryCode={userCountry}
                size={32}
                isElite={userIsElite}
                showAnimatedBorder={userShowAnimatedBorder}
              />
              {username ? (
                <UsernameLink username={username} isElite={userIsElite} className="user-name-desktop user-name--clickable">
                  <strong>{displayName}</strong>
                </UsernameLink>
              ) : profileUrl ? (
                <a href={profileUrl} className="user-name-desktop user-name--clickable">
                  <strong>{displayName}</strong>
                </a>
              ) : (
                <p className="user-name-desktop">
                  <strong>{displayName}</strong>
                </p>
              )}
            </div>
            <Points points={userPoints || 0} type="small" />
          </div>
          <div className="artist-bottom-info-responsive">
            {onRemoveAll && (
              <MagicButton
                label={isRemoved ? "Reset" : "Remove All"}
                icon={
                  isRemoved ? (
                    <img
                      src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/reset.svg"
                      alt="Reset"
                      style={{ width: 16, height: 16 }}
                    />
                  ) : (
                    <img
                      src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                      alt="Remove"
                      style={{ width: 16, height: 16 }}
                    />
                  )
                }
                color="dark-grey"
                fullWidth={true}
                onPress={isRemoved ? (onReset || (() => {})) : onRemoveAll}
              />
            )}
            <MagicButton
              label="Send"
              icon={
                <img
                  src={AssetUrls.icons.misc.sendPackage}
                  alt="Send Icon"
                  style={{ width: 20, height: 20 }}
                />
              }
              color="purple"
              fullWidth={true}
              onPress={onSend || onRemoveAll || (() => {})}
            />
          </div>
        </div>
      </div>

      {/* Right side: Header and Cards */}
      <div className="card-group-right-responsive">
        <CardGroupHeaderResponsive
          title={title}
          lowPrice={lowPrice}
          highPrice={highPrice}
          totalValue={totalValue}
          autoMatch={autoMatch}
          onAutoMatchToggle={onAutoMatchToggle}
          autoMatchDisabled={autoMatchDisabled}
          matchingCardCount={matchingCardCount}
          totalCardCount={totalCardCount}
          mode={mode}
          action={headerAction}
        />
        <div className="card-group-body-responsive">
          {children || (
            <div className="card-grid">
              {placeholderCards.map((card, index) => (
                <CardItem key={index} image={card.image} points={card.points} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardGroupContainerResponsive;
